import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2,
  CheckCircle2,
  FileText,
  FolderOpen,
  Info,
  Layers,
  LayoutGrid,
  Tag,
  Upload,
  WandSparkles,
  X,
} from "lucide-react";
import client, { unwrap, apiErrorMessage } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { titleCase } from "@/lib/format";
import { PDF_IMPORT_IMAGE } from "@/lib/stockImages";

const PRODUCT_TYPES = ["CURTAIN", "BLIND", "ROMAN_BLIND"];
const MAX_PDF_BYTES = 100 * 1024 * 1024;

const emptyImport = () => ({ companyName: "", versionName: "", defaultProductType: "CURTAIN", file: null });

const formatSize = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const HIGHLIGHTS = [
  [FileText, "Automatically extract designs, shades and specifications"],
  [LayoutGrid, "Create a draft catalogue for review before publishing"],
  [CheckCircle2, "Works with most supplier PDFs"],
];

function Field({ label, required, htmlFor, children }) {
  return (
    <div className="min-w-0">
      <label htmlFor={htmlFor} className="block text-sm text-foreground mb-1.5">
        {label} {required && <span className="text-danger">*</span>}
      </label>
      {children}
    </div>
  );
}

function IconInput({ icon: Icon, className, ...props }) {
  return (
    <div className="relative">
      <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" strokeWidth={1.75} />
      <input
        {...props}
        className={cn(
          "w-full h-11 rounded-lg border border-border bg-card pl-10 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20",
          className,
        )}
      />
    </div>
  );
}

function PdfDropzone({ file, onFile }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const accept = (picked) => {
    if (!picked) return;
    if (picked.type !== "application/pdf" && !picked.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please choose a PDF file");
      return;
    }
    if (picked.size > MAX_PDF_BYTES) {
      toast.error("That PDF is larger than 100 MB");
      return;
    }
    onFile(picked);
  };

  return (
    <div className="space-y-3">
      <div
        role="button"
        tabIndex={0}
        data-testid="import-dropzone"
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), inputRef.current?.click())}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          accept(e.dataTransfer.files?.[0]);
        }}
        className={cn(
          "flex flex-col items-center text-center rounded-xl border-2 border-dashed px-6 py-5 cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
          dragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 bg-background/40",
        )}
      >
        <span className="h-14 w-14 rounded-full bg-secondary flex items-center justify-center text-primary">
          <Upload className="h-6 w-6" strokeWidth={1.75} />
        </span>
        <p className="mt-3 font-semibold text-foreground">Drag & drop your PDF here</p>
        <p className="text-xs text-muted-foreground mt-0.5">or click to browse</p>
        <p className="text-xs text-muted-foreground mt-2">Supports PDF up to 100 MB</p>
        <span className="mt-3 inline-flex items-center gap-2 rounded-lg bg-secondary px-5 py-2 text-sm font-medium text-foreground">
          <FolderOpen className="h-4 w-4" strokeWidth={1.75} /> Choose PDF File
        </span>
        <input
          ref={inputRef}
          data-testid="import-file-input"
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={(e) => {
            accept(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
      </div>

      {file && (
        <div data-testid="import-selected-file" className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3">
          <span className="h-10 w-9 rounded-md bg-danger text-white text-[10px] font-bold flex items-center justify-center shrink-0">PDF</span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground truncate">{file.name}</p>
            <p className="text-xs text-muted-foreground">{formatSize(file.size)}</p>
          </div>
          <button
            type="button"
            aria-label="Remove file"
            data-testid="import-remove-file"
            onClick={() => onFile(null)}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

export default function ImportFromPdfDialog() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyImport());

  // Same key the catalogues page already loads, so this is served from cache.
  const { data: catalogues = [] } = useQuery({ queryKey: ["catalogues"], queryFn: () => client.get("/catalogues").then(unwrap) });
  const companies = [...new Set(catalogues.map((c) => c.companyName).filter(Boolean))].sort((a, b) => a.localeCompare(b));

  const startImport = useMutation({
    mutationFn: async () => {
      const formData = new FormData();
      formData.append("companyName", form.companyName);
      formData.append("versionName", form.versionName);
      formData.append("defaultProductType", form.defaultProductType);
      formData.append("file", form.file);
      const res = await client.post("/catalogues/imports", formData, { headers: { "Content-Type": "multipart/form-data" } });
      return unwrap(res);
    },
    onSuccess: (record) => {
      toast.success("PDF uploaded. Extracting products…");
      setOpen(false);
      setForm(emptyImport());
      queryClient.invalidateQueries({ queryKey: ["catalogue-imports"] });
      navigate(`/catalogues/imports/${record._id}`);
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Upload failed")),
  });

  const ready = form.companyName.trim() && form.versionName.trim() && form.file;
  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="import-pdf-button" className="rounded-full px-5">
          <Upload className="h-4 w-4 mr-1.5" /> Upload Catalogue
        </Button>
      </DialogTrigger>
      <DialogContent
        data-testid="import-pdf-dialog"
        className="max-w-5xl p-0 gap-0 overflow-hidden rounded-2xl sm:rounded-2xl bg-card md:grid-cols-[2fr_3fr] [&>button:last-child]:right-5 [&>button:last-child]:top-5"
      >
        <aside className="relative hidden md:flex flex-col justify-end bg-secondary">
          <img src={PDF_IMPORT_IMAGE} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--sidebar))] from-40% via-[hsl(var(--sidebar))]/85 via-55% to-transparent to-75%" />
          <div className="relative px-9 pb-10">
            <p className="font-heading text-[28px] leading-tight text-foreground">Turn supplier catalogues into digital collections</p>
            <ul className="mt-6 space-y-4">
              {HIGHLIGHTS.map(([Icon, text]) => (
                <li key={text} className="flex items-start gap-3.5 text-sm text-foreground/80">
                  <Icon className="h-6 w-6 shrink-0 text-foreground/70" strokeWidth={1.5} />
                  <span className="pt-0.5">{text}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <div className="flex flex-col max-h-[92vh] overflow-y-auto px-6 py-7 sm:px-9">
          <div className="flex items-start gap-4 sm:gap-5 pr-8 pb-6 border-b border-border">
            <span className="h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-secondary flex items-center justify-center text-primary shrink-0">
              <FileText className="h-6 w-6 sm:h-7 sm:w-7" strokeWidth={1.5} />
            </span>
            <div>
              <DialogTitle className="font-heading text-2xl sm:text-[28px] font-normal leading-tight text-foreground">Import Catalogue from PDF</DialogTitle>
              <DialogDescription className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                Upload a supplier catalogue. Designs, shades, specs and swatch images are extracted into a draft for your review before publishing.
              </DialogDescription>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-5 pt-6">
            <Field label="Catalogue Company" required htmlFor="import-company">
              <IconInput
                id="import-company"
                icon={Building2}
                list="import-company-options"
                data-testid="import-company-input"
                value={form.companyName}
                onChange={update("companyName")}
                placeholder="e.g. NuHome"
                autoComplete="off"
              />
              <datalist id="import-company-options">
                {companies.map((c) => <option key={c} value={c} />)}
              </datalist>
            </Field>
            <Field label="Catalogue / Collection Name" required htmlFor="import-version">
              <IconInput
                id="import-version"
                icon={Tag}
                data-testid="import-version-input"
                value={form.versionName}
                onChange={update("versionName")}
                placeholder="e.g. Zardosi, Ibiza Vol-II"
              />
            </Field>
            <Field label="Default Product Type" required>
              <Select value={form.defaultProductType} onValueChange={(v) => setForm({ ...form, defaultProductType: v })}>
                <SelectTrigger data-testid="import-type-select" className="h-11 rounded-lg bg-card">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Layers className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.75} />
                    <SelectValue />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {PRODUCT_TYPES.map((t) => <SelectItem key={t} value={t}>{titleCase(t)}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1.5">Used where the PDF doesn't say.</p>
            </Field>
          </div>

          <div className="pt-5">
            <Field label="Upload PDF Catalogue" required>
              <PdfDropzone file={form.file} onFile={(file) => setForm({ ...form, file })} />
            </Field>
          </div>

          <div className="mt-4 flex items-start gap-3 rounded-xl bg-primary/5 px-4 py-3">
            <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <p className="text-xs text-foreground/80 leading-relaxed">
              A draft catalogue will be created with extracted designs, shades and specifications. You can review, edit and publish it afterwards.
            </p>
          </div>

          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <DialogClose asChild>
              <Button variant="outline" className="h-11 rounded-xl px-10">Cancel</Button>
            </DialogClose>
            <Button
              data-testid="import-submit-button"
              disabled={!ready || startImport.isPending}
              className="h-11 rounded-xl px-7"
              onClick={() => startImport.mutate()}
            >
              <WandSparkles className="h-4 w-4 mr-2" />
              {startImport.isPending ? "Uploading…" : "Upload and Extract"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
